// Text/reasoning part renderer for the chat surface. Two paths, both
// security-critical and carried over unchanged from the deleted
// MessageBody.tsx/MessageBubble.tsx:
//  - markdown (default): StreamdownTextPrimitive, assistant-ui's integrated
//    wrapper around the `streamdown` markdown renderer (tuned for LLM
//    streaming — parses incomplete/malformed markdown mid-stream instead of
//    flashing raw tokens), with the `code`/`math`/`mermaid` plugins for Shiki
//    syntax highlighting, KaTeX math, and INLINE ```mermaid fences that
//    appear inside prose. This is distinct from the standalone Mermaid.tsx
//    generative-UI component, which renders whole-diagram data parts emitted
//    by an agent outside of markdown text.
//  - html (rare, `providerMetadata.agenthub.format === 'html'`): fail-closed
//    DOMPurify sanitize before dangerouslySetInnerHTML — untrusted HTML from
//    any source (assistant, agents, backend) is treated as untrusted.

import { StreamdownTextPrimitive } from '@assistant-ui/react-streamdown';
import type {
  CodeHeaderProps,
  ControlsConfig,
  MermaidErrorComponentProps,
  MermaidOptions,
  PluginConfig,
  RemendConfig,
  SecurityConfig,
  SyntaxHighlighterProps,
  StreamdownTextComponents,
} from '@assistant-ui/react-streamdown';
import { code } from '@streamdown/code';
import { cjk } from '@streamdown/cjk';
import { math } from '@streamdown/math';
import { mermaid } from '@streamdown/mermaid';
import 'katex/dist/katex.min.css';
import { useAuiState } from '@assistant-ui/react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useState, type FC } from 'react';
import { sanitizeHtml } from '@/shared/lib';
import {
  MarkdownLink,
  SafeImage,
  MarkdownBlockquote,
  MarkdownH1,
  MarkdownH2,
  MarkdownH3,
  MarkdownH4,
  MarkdownParagraph,
  MarkdownUl,
  MarkdownOl,
  MarkdownLi,
  MarkdownHr,
  MarkdownStrong,
  MarkdownEm,
} from './MarkdownComponents';
import { isAllowedMarkdownHref } from './markdownSafety';
import { normalizeAssistantMarkdown } from '../model/normalizeAssistantMarkdown';

// Module-level constants (not built inline in JSX) so StreamdownTextPrimitive
// sees stable `components`/`plugins`/`security` references across renders
// instead of new objects every token of a streaming response.
const streamdownPlugins: PluginConfig = { code, math, mermaid, cjk };
const streamdownUserPlugins: PluginConfig = { code, math, cjk };
const streamdownControls: ControlsConfig = {
  code: true,
  table: true,
  mermaid: {
    copy: true,
    download: true,
    fullscreen: true,
    panZoom: true,
  },
};
const streamdownRemend: RemendConfig = {
  links: true,
  images: true,
  linkMode: 'protocol',
  bold: true,
  italic: true,
  boldItalic: true,
  inlineCode: true,
  strikethrough: true,
  katex: true,
  setextHeadings: true,
  handlers: [],
};

// Raw HTML is limited to presentational tags and attributes. URL-bearing
// elements remain unavailable here; links and images use the existing
// MarkdownLink/SafeImage renderers.
const streamdownAllowedTags = {
  div: ['class', 'id'],
  span: ['class'],
  mark: ['class'],
  kbd: ['class'],
  sub: [],
  sup: [],
};

const streamdownLinkSafety = {
  enabled: true,
  onLinkCheck: isAllowedMarkdownHref,
};

// Leaving `security` unset makes StreamdownTextPrimitive fall back to the
// underlying `streamdown` package's own default `rehype-harden` pass, whose
// `defaultOrigin` is `undefined`. That default pass runs at the markdown
// AST level -- before MarkdownLink ever sees the node -- and drops any
// scheme-less relative href that doesn't start with `/`, `./`, or `../`
// (e.g. a bare `outputs/report.pdf` artifact reference the model echoed
// verbatim), replacing the link with a literal "<label> [blocked]" span.
// Setting `defaultOrigin` lets rehype-harden resolve those same relative
// hrefs against our own origin instead of discarding them; the actual
// allow/deny decision still lives in isAllowedMarkdownHref/MarkdownLink and
// SafeImage, so prefixes/protocols stay wildcarded here to avoid a second,
// out-of-sync allowlist.
const streamdownSecurity: SecurityConfig = {
  allowedLinkPrefixes: ['*'],
  allowedImagePrefixes: ['*'],
  allowedProtocols: ['*'],
  allowDataImages: true,
  defaultOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
};

function normalizeUserPromptMarkdown(text: string): string {
  return text.replace(/^\s+/, '');
}

// Streamdown caps tables at 300px by default, which hid all but the first
// row or two of the priority tables that lead most synthesized answers.
// Tables grow to their content instead; wide tables still scroll
// horizontally and the built-in fullscreen control remains for long ones.
const TABLE_MAX_HEIGHT_DISABLED = 0;

function MermaidError({ retry }: MermaidErrorComponentProps) {
  return (
    <div className="my-4 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
      <p className="mb-2">This diagram could not be rendered.</p>
      <button type="button" className="text-primary underline underline-offset-2 hover:no-underline" onClick={retry}>
        Retry
      </button>
    </div>
  );
}

const streamdownMermaid: MermaidOptions = {
  config: {
    theme: 'neutral',
    securityLevel: 'strict',
  },
  errorComponent: MermaidError,
};

const markdownComponents = {
  a: MarkdownLink,
  img: SafeImage,
  blockquote: MarkdownBlockquote,
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  p: MarkdownParagraph,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  hr: MarkdownHr,
  strong: MarkdownStrong,
  em: MarkdownEm,
  // No `code`/`pre` overrides — see the comment in MarkdownComponents.tsx for
  // why: the `@streamdown/code` plugin owns that element directly.
  // No `table` overrides either: Streamdown's own table wrapper provides
  // copy/download/fullscreen controls, and a replacement table component would
  // silently remove those built-in affordances.
};

const StreamdownSyntaxHighlighter: FC<SyntaxHighlighterProps> = ({ components: codeComponents, language, code: source }) => {
  const Pre = codeComponents.Pre;
  const Code = codeComponents.Code;
  return (
    <Pre className="aui-streamdown-code overflow-x-auto rounded-b-xl border border-t-0 border-border/50 bg-muted/30 p-3.5 text-[13px] leading-relaxed">
      <Code className="font-mono" data-language={language}>{source}</Code>
    </Pre>
  );
};

const StreamdownCodeHeader: FC<CodeHeaderProps> = ({ language, code: source }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!source || !navigator.clipboard) return;
    void navigator.clipboard.writeText(source).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    });
  };
  return (
    <div className="aui-streamdown-code-header mt-3 flex select-none items-center justify-between rounded-t-xl border border-border/50 bg-muted/50 px-3.5 py-1.5 text-xs">
      <span className="text-muted-foreground font-medium lowercase">{language ?? 'code'}</span>
      <button type="button" aria-label={copied ? 'Code copied' : 'Copy code'} className="text-muted-foreground hover:text-foreground" onClick={copy}>
        {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
      </button>
    </div>
  );
};

const streamdownComponents = {
  ...markdownComponents,
  SyntaxHighlighter: StreamdownSyntaxHighlighter,
  CodeHeader: StreamdownCodeHeader,
} as unknown as StreamdownTextComponents;

export function MarkdownText() {
  const part = useAuiState((s) => s.part);
  const isRunning = useAuiState((s) => s.message.status?.type === 'running');
  if (part.type !== 'text' && part.type !== 'reasoning') return null;

  const format = part.type === 'text' ? part.providerMetadata?.agenthub?.format : undefined;

  if (format === 'html') {
    return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(part.text) }} />;
  }
  if (format === 'text') {
    return <div>{part.text}</div>;
  }

  // StreamdownTextPrimitive reads its text from the current message-part
  // context (`useMessagePartText`) rather than a `children` prop — it must
  // render inside this same part context, which it does here.
  return (
    <StreamdownTextPrimitive
      mode={isRunning ? 'streaming' : 'static'}
      // Keep streaming Markdown parsing, but do not replay the accumulated
      // response on every token update.
      smooth={false}
      plugins={streamdownPlugins}
      shikiTheme={['github-light', 'github-dark']}
      remend={streamdownRemend}
      mermaid={streamdownMermaid}
      caret={isRunning ? 'circle' : undefined}
      linkSafety={streamdownLinkSafety}
      security={streamdownSecurity}
      allowedTags={streamdownAllowedTags}
      components={streamdownComponents}
      controls={streamdownControls}
      // Repairs header/delimiter column mismatches the model sometimes emits
      // (which would otherwise render the whole table as raw pipe text) and
      // numeric tokens split around their decimal point ("$52. 3M").
      preprocess={normalizeAssistantMarkdown}
      tableMaxHeight={TABLE_MAX_HEIGHT_DISABLED}
    />
  );
}

export function UserMarkdownText() {
  const part = useAuiState((s) => s.part);
  if (part.type !== 'text') return null;

  return (
    <StreamdownTextPrimitive
      mode="static"
      plugins={streamdownUserPlugins}
      shikiTheme={['github-light', 'github-dark']}
      linkSafety={streamdownLinkSafety}
      security={streamdownSecurity}
      allowedTags={streamdownAllowedTags}
      remend={streamdownRemend}
      preprocess={normalizeUserPromptMarkdown}
      components={markdownComponents}
      controls={false}
      lineNumbers={false}
    />
  );
}
